import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import SEO from '../../components/SEO';
import { 
  ArrowRight, CheckCircle, TrendingUp, TrendingDown,
  Shield, Target, Bell, Brain, Zap, BarChart3,
  ChevronRight, Sparkles, Activity, LineChart, Clock, Users
} from 'lucide-react';

// FAQ for SEO
const faqData = [
  {
    question: "Kripto sinyali nedir?",
    answer: "Kripto sinyali, belirli bir kripto para icin alim veya satim onerisi iceren bildirimdir. Giris fiyati, hedef (TP) ve zarar kesme (SL) seviyelerini icerir. Profesyonel traderlar veya algoritmalar tarafindan uretilir."
  },
  {
    question: "Kripto sinyalleri nasil kullanilir?",
    answer: "Sinyal geldiginde coin ve yonu (long/short) kontrol edin, belirlenen giris fiyatindan pozisyon acin, stop-loss emrinizi koyun ve hedef seviyelerde kademeli kar alin. Her zaman risk yonetimi uygulayin."
  },
  {
    question: "Kripto sinyalleri ucretsiz mi?",
    answer: "Bircok sinyal platformu ucretsiz plan sunmaktadir. Temel sinyallere erisim saglayan ucretsiz planlar, anlik bildirimler ve detayli analizler icin ise genellikle premium uyelik gerekmektedir."
  },
  {
    question: "Hangi coinler icin sinyal veriliyor?",
    answer: "Kaliteli sinyal platformlari yuksek hacimli ve likit coinleri tarar: BTC, ETH, SOL, XRP, ADA, AVAX, LINK, DOT ve daha fazlasi. Dusuk hacimli pump-dump riski tasiyan coinlerden genellikle kacinilir."
  },
  {
    question: "Sinyaller ne kadar basarili?",
    answer: "Hicbir sistem yuzde yuz garanti veremez. Guvenilebilir platformlar seffaf performans verileri sunar ve tum sonuclari (kazanan ve kaybeden) acikca gosterir. Gecmis performans gelecek sonuclari garanti etmez."
  }
];

const structuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqData.map(item => ({
    "@type": "Question",
    "name": item.question,
    "acceptedAnswer": { "@type": "Answer", "text": item.answer }
  }))
};

const KriptoSinyalleriPage = () => {
  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO 
        title="Kripto Sinyalleri – Profesyonel Trading Sinyalleri | Borsamex"
        description="Kripto sinyalleri ile trading fırsatlarını yakalayın. Giriş, hedef ve stop-loss seviyeleri ile risk yönetimli sinyaller. Ücretsiz deneyin."
        keywords="kripto sinyalleri, kripto sinyal, bitcoin sinyali, altcoin sinyalleri, trading sinyalleri, kripto al sat sinyalleri"
        canonical="https://borsamex.com/kripto-sinyalleri"
        structuredData={structuredData}
      />

      {/* HERO */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 via-transparent to-brand-green/5" />
        <div className="container mx-auto px-4 max-w-5xl relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <Badge className="mb-6 px-4 py-2 bg-blue-500/10 text-blue-400 border-blue-500/20">
              <Activity className="h-4 w-4 mr-2" />
              7/24 Aktif Tarama
            </Badge>
            
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6 leading-tight">
              Kripto Sinyalleri<br />
              <span className="text-brand-green">Profesyonel Trading Rehberi</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
              <strong className="text-white">Kripto sinyalleri</strong> ile piyasa fırsatlarını kaçırmayın. 
              Her sinyal <strong className="text-brand-green">giriş fiyatı</strong>, 
              <strong className="text-brand-green"> hedef seviyeleri</strong> ve 
              <strong className="text-brand-green"> stop-loss</strong> içerir.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8">
                  Ücretsiz Başla <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/signals">
                <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                  Sinyalleri Gör
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Kripto Sinyali Nedir */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
                Kripto Sinyali Nedir?
              </h2>
              <p className="text-slate-300 mb-4">
                Kripto sinyali, belirli bir kripto para birimi için <strong className="text-white">alım veya satım önerisi</strong> içeren 
                bir bildirimdir. Profesyonel bir sinyal şunları içerir:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-brand-green mt-0.5" />
                  <span className="text-slate-300"><strong className="text-white">Coin:</strong> Hangi kripto para (örn: BTC, ETH)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-brand-green mt-0.5" />
                  <span className="text-slate-300"><strong className="text-white">Yön:</strong> Long (AL) veya Short (SAT)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-brand-green mt-0.5" />
                  <span className="text-slate-300"><strong className="text-white">Giriş:</strong> Pozisyon açılacak fiyat seviyesi</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-brand-green mt-0.5" />
                  <span className="text-slate-300"><strong className="text-white">Hedefler (TP):</strong> Kar alınacak seviyeler</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-brand-green mt-0.5" />
                  <span className="text-slate-300"><strong className="text-white">Stop-Loss:</strong> Zarar kesme seviyesi</span>
                </li>
              </ul>
            </div>
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <Badge className="bg-green-500/20 text-green-400">LONG</Badge>
                <span className="text-slate-400 text-sm">Örnek Sinyal</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">BTC-USDT</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-400">Giriş</span>
                  <span className="text-white font-mono">$64,200</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Hedef 1</span>
                  <span className="text-brand-green font-mono">$65,800</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Hedef 2</span>
                  <span className="text-brand-green font-mono">$67,500</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Hedef 3</span>
                  <span className="text-brand-green font-mono">$70,000</span>
                </div>
                <div className="flex justify-between border-t border-slate-700 pt-3">
                  <span className="text-slate-400">Stop-Loss</span>
                  <span className="text-red-400 font-mono">$62,500</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nasıl Çalışır */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Kripto Sinyalleri Nasıl Çalışır?
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-brand-green/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-brand-green font-bold">1</span>
                </div>
                <h3 className="text-white font-bold mb-2">Piyasa Taranır</h3>
                <p className="text-slate-400 text-sm">
                  Sniper Engine 7/24 piyasayı tarar ve potansiyel fırsatları tespit eder.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-brand-green/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-brand-green font-bold">2</span>
                </div>
                <h3 className="text-white font-bold mb-2">Analiz Yapılır</h3>
                <p className="text-slate-400 text-sm">
                  200+ teknik gösterge hesaplanır, destek-direnç seviyeleri belirlenir.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-brand-green/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-brand-green font-bold">3</span>
                </div>
                <h3 className="text-white font-bold mb-2">Sinyal Üretilir</h3>
                <p className="text-slate-400 text-sm">
                  Kalite skorunu geçen setuplar sinyal olarak yayınlanır.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-brand-green/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-brand-green font-bold">4</span>
                </div>
                <h3 className="text-white font-bold mb-2">Bildirim Gönderilir</h3>
                <p className="text-slate-400 text-sm">
                  Telegram ve uygulama üzerinden anlık bildirim alırsınız.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Sinyal Türleri */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Kripto Sinyal Türleri
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Link to="/long-sinyaller">
              <Card className="bg-slate-800/50 border-slate-700 hover:border-green-500/50 transition-colors h-full">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                      <TrendingUp className="h-6 w-6 text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Long Sinyalleri</h3>
                      <p className="text-green-400 text-sm">Yükseliş beklentisi</p>
                    </div>
                  </div>
                  <p className="text-slate-400">
                    Fiyatın yükseleceği beklenen durumlarda verilen alım sinyalleri. 
                    Düşükten alıp yükseğe satma stratejisi.
                  </p>
                  <span className="text-brand-green text-sm mt-4 inline-flex items-center">
                    Long sinyallerini gör <ChevronRight className="h-4 w-4 ml-1" />
                  </span>
                </CardContent>
              </Card>
            </Link>

            <Link to="/short-sinyaller">
              <Card className="bg-slate-800/50 border-slate-700 hover:border-red-500/50 transition-colors h-full">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                      <TrendingDown className="h-6 w-6 text-red-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Short Sinyalleri</h3>
                      <p className="text-red-400 text-sm">Düşüş beklentisi</p>
                    </div>
                  </div>
                  <p className="text-slate-400">
                    Fiyatın düşeceği beklenen durumlarda verilen satış sinyalleri. 
                    Vadeli işlemlerde düşüşten kar etme stratejisi.
                  </p>
                  <span className="text-brand-green text-sm mt-4 inline-flex items-center">
                    Short sinyallerini gör <ChevronRight className="h-4 w-4 ml-1" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Hangi Coinler */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Hangi Coinler İçin Sinyal Veriliyor?
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Yüksek hacimli, likit ve güvenilir coinler taranır. 
              Düşük hacimli pump-dump riski taşıyan coinlerden kaçınılır.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {['BTC', 'ETH', 'SOL', 'XRP', 'ADA', 'AVAX', 'LINK', 'DOT', 'MATIC', 'UNI', 'AAVE', 'NEAR', 'APT', 'FET', 'INJ'].map(coin => (
              <Badge key={coin} className="px-4 py-2 bg-slate-800 text-slate-300 border-slate-700 text-sm">
                {coin}
              </Badge>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/altcoin-sinyalleri">
              <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                Altcoin Sinyalleri <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Sıkça Sorulan Sorular
            </h2>
          </div>

          <div className="space-y-4">
            {faqData.map((faq, idx) => (
              <Card key={idx} className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-6">
                  <h3 className="text-white font-bold mb-2">{faq.question}</h3>
                  <p className="text-slate-400 text-sm">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-b from-brand-green/10 to-transparent">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Kripto Sinyallerini Ücretsiz Deneyin
          </h2>
          <p className="text-slate-300 mb-8">
            Hesap oluşturun ve profesyonel kripto sinyallerine hemen erişin. 
            Kredi kartı gerekmez.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8">
                Ücretsiz Başla <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/en-iyi-kripto-sinyalleri">
              <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                Platform Karşılaştır
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default KriptoSinyalleriPage;
